import { useMemo } from 'react';
import { ethers } from 'ethers';
import { CONTRACT_ADDRESS, ESCROW_ABI } from '../services/contracts/config';
import { contractMethods } from '../services/contracts/contracts';

interface UseContractReturn {
  contract: ethers.Contract | null;
  createProject: (
    provider: string,
    totalAmount: bigint,
    descriptions: string[],
    amounts: bigint[]
  ) => Promise<any>;
  depositFunds: (projectId: number, amount: bigint) => Promise<any>;
  approveMilestone: (projectId: number, milestoneId: number) => Promise<any>;
  releasePayment: (projectId: number, milestoneId: number) => Promise<any>;
  getProject: (projectId: number) => Promise<any>;
  getMilestones: (projectId: number) => Promise<any>;
  openDispute: (projectId: number, milestoneId: number, reason: string) => Promise<any>;
  setArbitrator: (projectId: number, arbitratorAddress: string) => Promise<any>;
  resolveDispute: (
    projectId: number,
    milestoneId: number,
    clientSharePercent: number,
    providerSharePercent: number
  ) => Promise<any>;
}

export const useContract = (signer: ethers.JsonRpcSigner | null): UseContractReturn => {
  const contract = useMemo(() => {
    if (!signer || !CONTRACT_ADDRESS) {
      return null;
    }
    try {
      return new ethers.Contract(CONTRACT_ADDRESS, ESCROW_ABI, signer);
    } catch (error) {
      console.error('Error creating contract:', error);
      return null;
    }
  }, [signer]);

  const createProject = async (
    provider: string,
    totalAmount: bigint,
    descriptions: string[],
    amounts: bigint[]
  ) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.createProject(signer, provider, totalAmount, descriptions, amounts);
  };

  const depositFunds = async (projectId: number, amount: bigint) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.depositFunds(signer, projectId, amount);
  };

  const approveMilestone = async (projectId: number, milestoneId: number) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.approveMilestone(signer, projectId, milestoneId);
  };

  const releasePayment = async (projectId: number, milestoneId: number) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.releasePayment(signer, projectId, milestoneId);
  };

  const getProject = async (projectId: number) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.getProject(signer, projectId);
  };

  const getMilestones = async (projectId: number) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.getMilestones(signer, projectId);
  };

  const openDispute = async (projectId: number, milestoneId: number, reason: string) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.openDispute(signer, projectId, milestoneId, reason);
  };

  const setArbitrator = async (projectId: number, arbitratorAddress: string) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.setArbitrator(signer, projectId, arbitratorAddress);
  };

  const resolveDispute = async (
    projectId: number,
    milestoneId: number,
    clientSharePercent: number,
    providerSharePercent: number
  ) => {
    if (!signer) {
      throw new Error('Signer not available');
    }
    return contractMethods.resolveDispute(signer, projectId, milestoneId, clientSharePercent, providerSharePercent);
  };

  return {
    contract,
    createProject,
    depositFunds,
    approveMilestone,
    releasePayment,
    getProject,
    getMilestones,
    openDispute,
    setArbitrator,
    resolveDispute,
  };
};

